import React, { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { X, FileText } from "lucide-react";
import { IOrder } from "../../models/Order.model";

import toast from "react-hot-toast";
import { verifyPaymentByAccount } from "../../store/slices/orderSlice";
import { useDispatch } from "react-redux";
import { RootDispatch } from "../../store";
import { IEpcPayment } from "../../models/epcCustomer.model";
import Button, { ButtonType } from "../../assets/Button";

type ScreenshotModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedOrder: IOrder | null;
  fetchPayments: () => Promise<void>;
  selectedEpcPayment?: IEpcPayment | null;
};

const ScreenshotModal: React.FC<ScreenshotModalProps> = ({
  isOpen,
  onClose,
  selectedOrder,
  fetchPayments,
  selectedEpcPayment,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const [selectedScreenshot, setSelectedScreenshot] = useState<string | null>(
    null,
  );

  const handleScreenshotSelection = (screenshotKey: string) => {
    // If clicking on already selected screenshot, unselect it
    if (selectedScreenshot === screenshotKey) {
      setSelectedScreenshot(null);
    } else {
      // Otherwise, select this screenshot (automatically unselects previous)
      setSelectedScreenshot(screenshotKey);
    }
  };

  const handleConfirm = async (type: "utr" | "screenshot", key: string) => {
    if (!selectedOrder) return;

    try {
      const payload = {
        orderId: selectedOrder._id,
        utrNumbers: type === "utr" ? [key] : [],
        screenshots: type === "screenshot" ? [key] : [],
      };

      if (payload.utrNumbers.length === 0 && payload.screenshots.length === 0) {
        toast.error("Please select at least one UTR or screenshot to verify.");
        return;
      }

      await dispatch(verifyPaymentByAccount(payload)).unwrap();
      await fetchPayments();

      onClose();
      // Reset selection after confirmation
      setSelectedScreenshot(null);

      toast.success("Payment verified successfully!");
    } catch (error) {
      toast.error("Failed to verify payment. Please try again.");
    }
  };

  const renderEpcProof = () => {
    const isPdf = selectedEpcPayment?.docType === "pdf";

    return (
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {/* Document Preview Section */}
        <div className="relative">
          {isPdf ? (
            <div className="h-40 bg-gray-50 flex flex-col items-center justify-center group hover:bg-gray-100/50 transition-colors">
              <div className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm mb-3">
                <FileText className="text-gray-700" size={20} />
              </div>
              <span className="text-sm text-gray-600 font-medium mb-4">
                {selectedEpcPayment?.paymentProofUrl?.split("/").pop() ||
                  "Payment Proof Document.pdf"}
              </span>
              <a
                href={selectedEpcPayment?.paymentProofUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                View Document
              </a>
            </div>
          ) : (
            <div className="relative h-40 overflow-hidden bg-gray-50">
              <img
                src={selectedEpcPayment?.paymentProofUrl}
                alt="Payment verification proof"
                className="w-full h-full object-cover cursor-pointer"
                onClick={() =>
                  window.open(selectedEpcPayment?.paymentProofUrl, "_blank")
                }
              />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
                <button
                  onClick={() =>
                    window.open(selectedEpcPayment?.paymentProofUrl, "_blank")
                  }
                  className="bg-white hover:bg-gray-50 text-gray-900 text-sm font-medium px-4 py-2 rounded-md border border-gray-200 shadow-sm transition-colors inline-flex items-center gap-2"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  View Full
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Payment Details Section */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Customer
              </label>
              <p
                className="text-sm font-medium text-gray-900 truncate"
                title={selectedEpcPayment?.customerName}
              >
                {selectedEpcPayment?.customerName}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Amount Paid
              </label>
              <p className="text-sm font-semibold text-gray-900">
                ₹{selectedEpcPayment?.paymentPaid?.toLocaleString() || "0"}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Total Bill
              </label>
              <p className="text-sm font-medium text-gray-900">
                ₹{selectedEpcPayment?.totalBillAmount?.toLocaleString() || "0"}
              </p>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Sales Rep
              </label>
              <p
                className="text-sm font-medium text-gray-900 truncate"
                title={
                  typeof selectedEpcPayment?.salesRepresentative === "object"
                    ? selectedEpcPayment?.salesRepresentative?.name
                    : ""
                }
              >
                {typeof selectedEpcPayment?.salesRepresentative === "object"
                  ? selectedEpcPayment?.salesRepresentative?.name
                  : ""}
              </p>
            </div>
          </div>

          {/* Payment Status Badge */}
          {selectedEpcPayment?.paymentPaid &&
            selectedEpcPayment?.totalBillAmount && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Payment Status
                  </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${
                      selectedEpcPayment.paymentPaid >=
                      selectedEpcPayment.totalBillAmount
                        ? "bg-gray-50 text-gray-700 border-gray-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                    }`}
                  >
                    {selectedEpcPayment.paymentPaid >=
                    selectedEpcPayment.totalBillAmount
                      ? "Fully Paid"
                      : "Partial Payment"}
                  </span>
                </div>
              </div>
            )}
        </div>
      </div>
    );
  };
  const renderOrderScreenshots = () => {
    return (
      <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
        {Object.entries(selectedOrder?.paymentDetails?.paymentScreenshot || {})
          .length === 0 ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center mx-auto mb-3">
              <FileText className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">No screenshots found</p>
            <p className="text-xs text-gray-400">
              This order doesn't contain any payment screenshots
            </p>
          </div>
        ) : (
          <>
            {/* Pending Screenshots */}
            <h3 className="text-xs font-medium mb-2">
              Pending Verification (Select one at a time)
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {Object.entries(
                selectedOrder?.paymentDetails?.paymentScreenshot || {},
              )
                .filter(([_, data]: any) => data.isCompleted === false)
                .map(([screenshotKey, data]: any) => (
                  <div
                    key={screenshotKey}
                    className={`border rounded-lg p-3 flex flex-col h-full cursor-pointer transition-colors ${
                      selectedScreenshot === screenshotKey
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => handleScreenshotSelection(screenshotKey)}
                  >
                    <div className="relative mb-3">
                      {/* Selection indicator */}
                      <div className="absolute top-2 right-2 z-10">
                        <input
                          type="checkbox"
                          checked={selectedScreenshot === screenshotKey}
                          onChange={() =>
                            handleScreenshotSelection(screenshotKey)
                          }
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {data.fileType === "pdf" ? (
                        <div className="w-full h-24 bg-red-50 rounded border border-red-200 flex flex-col items-center justify-center">
                          <FileText className="text-red-500" size={20} />
                          <span className="text-xs text-red-600 mt-2 truncate w-full px-2 text-center">
                            {data.originalName}
                          </span>
                        </div>
                      ) : (
                        <img
                          src={data.url}
                          alt="Payment screenshot"
                          className="w-full h-24 rounded border border-gray-200 object-cover"
                        />
                      )}
                    </div>

                    <div className="mt-auto">
                      <p className="text-xs font-medium">
                        Amount: ₹{Number(data.amount).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500">
                        Filename:
                        {data?.originalName?.length > 15
                          ? `${data?.originalName?.slice(0, 15)}...`
                          : data?.originalName}
                      </p>
                      <a
                        href={data.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(event) => event.stopPropagation()}
                        className="mt-1 inline-block text-xs font-medium text-blue-700 hover:underline"
                      >
                        Open file
                      </a>

                      {selectedScreenshot === screenshotKey && (
                        <Button
                          type={ButtonType.Submit}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConfirm("screenshot", screenshotKey);
                          }}
                          className="mt-2 w-full !py-1.5 text-xs"
                        >
                          Approve
                        </Button>
                      )}
                    </div>
                  </div>
                ))}

              {Object.entries(
                selectedOrder?.paymentDetails?.paymentScreenshot || {},
              ).filter(([_, data]: any) => data.isCompleted === false)
                .length === 0 && (
                <p className="text-xs text-gray-500 text-center col-span-full py-4">
                  No pending screenshots to verify
                </p>
              )}
            </div>

            {/* Verified Screenshots */}
            <h3 className="text-xs font-medium mb-2">Verified Screenshots</h3>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(
                selectedOrder?.paymentDetails?.paymentScreenshot || {},
              )
                .filter(([_, data]: any) => data.isCompleted === true)
                .map(([screenshotKey, data]: any) => (
                  <div
                    key={screenshotKey}
                    className="relative group flex flex-col justify-between"
                  >
                    {data.fileType === "pdf" ? (
                      <div className="w-full h-24 bg-red-50 rounded border border-red-200 flex flex-col items-center justify-center">
                        <FileText className="text-red-500" size={20} />
                        <span className="text-xs text-red-600 mt-1 truncate w-full px-2 text-center">
                          {data.originalName}
                        </span>
                      </div>
                    ) : (
                      <img
                        src={data.url}
                        alt="Verified payment"
                        className="w-full h-24 rounded border border-gray-200 object-cover"
                      />
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <a
                        href={data.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white text-xs bg-navyBlue-primary px-2 py-1 rounded"
                      >
                        View Full
                      </a>
                    </div>
                    <div className="mt-1 text-xs">
                      <p>Amount: ₹{Number(data.amount).toFixed(2)}</p>
                      <p className="text-green-700">Verified</p>
                    </div>
                  </div>
                ))}

              {Object.entries(
                selectedOrder?.paymentDetails?.paymentScreenshot || {},
              ).filter(([_, data]: any) => data.isCompleted === true).length ===
                0 && (
                <p className="text-sm text-gray-500 text-center py-4 col-span-2">
                  No verified screenshots yet
                </p>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 w-full max-w-4xl shadow-xl relative max-h-[80vh] overflow-y-auto">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
            >
              <X />
            </button>
            <h2 className="text-lg font-semibold mb-4">
              {selectedEpcPayment ? "EPC Payment Proof" : "Payment Screenshots"}
            </h2>

            {/* Render content based on whether it's EPC or Order */}
            {selectedEpcPayment ? renderEpcProof() : renderOrderScreenshots()}
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ScreenshotModal;
